// Wire types for the Create flow (frameworks → runs). These mirror the backend
// Pydantic schemas (snake_case JSON), NOT the iOS camelCase structs — the web
// client consumes the raw API. Sources:
//   backend/app/schemas/framework.py, backend/app/schemas/run.py
//   backend/app/parts/headteacher_math_topic_plan.py  (topic-plan family)
//   backend/app/parts/headteacher_lecture_exercise_plan.py  (chip-rail)

export interface FrameworkStepSummary {
  index: number;
  part_slug: string;
  part_kind: string;
  description: string;
}

export interface FrameworkCost {
  level: string;
  note: string;
  credits_point?: number | null;
  credits_low?: number | null;
  credits_high?: number | null;
  expected_questions?: number | null;
  average_cost_per_question?: number | null;
  topic_count?: number | null;
  // model_config = extra="allow" on the backend; tolerate unknown fields.
  [key: string]: unknown;
}

export interface FrameworkRead {
  slug: string;
  title: string;
  description: string;
  steps: FrameworkStepSummary[];
  cost_estimate: FrameworkCost;
}

export interface StartInputField {
  name: string;
  type: string;
  required: boolean;
}

export interface FrameworkPipelinePreview {
  slug: string;
  steps: FrameworkStepSummary[];
  required_start_inputs: StartInputField[];
}

// ── Runs ────────────────────────────────────────────────────────────────────

export type RunStatus = "pending" | "running" | "awaiting_user" | "complete" | "failed";

export const IN_FLIGHT_STATUSES: RunStatus[] = ["pending", "running", "awaiting_user"];

export interface RunStepRead {
  index: number;
  part_slug: string;
  status: string;
  output: Record<string, unknown> | null;
  user_response: Record<string, unknown> | null;
  error_message: string | null;
  updated_at: string;
}

export interface RunRead {
  id: string;
  framework_slug: string;
  status: RunStatus;
  current_step_index: number;
  start_inputs: Record<string, unknown>;
  produced_question_set_id: string | null;
  error_message: string | null;
  steps: RunStepRead[];
  created_at: string;
  updated_at: string;
  total_cost_usd?: number | null;
  total_prompt_tokens?: number | null;
  total_completion_tokens?: number | null;
  model_mix?: Record<string, unknown> | null;
}

// Free-form like the backend's start_inputs dict. Most values are scalars or
// string arrays, but some frameworks pass a nested object (e.g. lecture's
// learning_goals = { preset, note }).
export type StartInputs = Record<
  string,
  string | number | boolean | string[] | Record<string, unknown> | null
>;

// ── Step payloads: topic-plan family ────────────────────────────────────────
// Parts: headteacher.topic-plan, headteacher.math-topic-plan,
// headteacher.de-grammar-topic-plan (schema-identical).

export type Depth = "already_know" | "recap_briefly" | "dig_deeper" | "from_scratch";

export interface TopicPlanSourceSpan {
  file_id?: string | null;
  excerpt: string;
}

/** A drill activity proposed for (or addable to) a topic. Only emitted by the
 *  language-trainer headteacher; absent for the other topic-plan families. */
export interface ProposedMode {
  mode_id: string;
  intensity?: string;
  info_beat?: boolean;
  rationale?: string;
}

/** Catalog entry for the language-trainer "add drill" picker. */
export interface AvailableMode {
  mode_id: string;
  label: string;
  summary: string;
}

export interface TopicPlanTopic {
  id: string;
  slug: string;
  label: string;
  summary: string;
  source_spans: TopicPlanSourceSpan[];
  depth_suggestion: Depth;
  question_count_suggestion: number;
  // Language-trainer only: the drills Eddi proposed for this topic.
  proposed_modes?: ProposedMode[];
}

export interface TopicPlanOutput {
  topics: TopicPlanTopic[];
  // Language-trainer only: per-language drill catalog for the add-drill picker.
  available_modes?: AvailableMode[];
}

export interface TopicPlanResponseTopic {
  topic_id: string;
  depth: Depth;
  question_count_override?: number | null;
}

export interface TopicPlanUserResponse {
  topics: TopicPlanResponseTopic[];
}

// ── Step payloads: chip-rail (lecture-exercise-plan) ────────────────────────

export type ChipMode = "deep" | "brief" | "test";
export type PhraseKind = "concept" | "theorem" | "method" | "definition" | "result";
export type BlockType = "text" | "phrase";
export type TopicImportance = "core" | "supporting";

export interface ChipRailSummaryBlock {
  type: BlockType;
  text: string;
  id?: string | null;
  kind?: PhraseKind | null;
}

export interface ChipRailTopic {
  id: string;
  label: string;
  summary: string;
  related_phrase_ids: string[];
  importance: TopicImportance;
}

export interface ChipRailOutput {
  summary_blocks: ChipRailSummaryBlock[];
  topics: ChipRailTopic[];
  goals: string[];
}

export interface ChipRailUserResponse {
  phrase_selections: { phrase_id: string; mode: ChipMode }[];
  topic_selections: { topic_id: string; mode: ChipMode }[];
  additional_focus: { label: string; mode: ChipMode }[];
}

// ── Step payloads: discussion planner (headteacher.discussion) ──────────────
// The unified multi-agent planner. Mirrors backend
// `backend/app/parts/discussion_schemas.py` (Proposal + DiscussionUserResponse,
// snake_case JSON). The user-response side is strict (`extra="forbid"`), so the
// submitted payload must contain ONLY the keys below. Source of truth for the
// 3-slide UX is iOS DiscussionReviewView.swift.

export interface DiscussionSummaryBlock {
  text: string;
  type?: string | null;
  id?: string | null;
}

export interface DiscussionKeyTerm {
  term: string;
  definition: string;
}

// NB: `ProposalBulk` carries no `index` on the wire — iOS assigns the index by
// array position, and `drop_bulk_indices` are positional indices into
// `ordered_bulks`. The web must do the same.
export interface DiscussionBulk {
  bulk_kind: string;
  question_types: string[];
  chosen_types: string[];
  focus: string;
}

export interface DiscussionTopic {
  id: string;
  slug: string;
  label: string;
  summary: string;
  depth: Depth;
  question_count: number;
  ordered_bulks: DiscussionBulk[];
  key_points: string[];
  key_terms: DiscussionKeyTerm[];
}

export interface DiscussionInfoCard {
  id: string;
  topic_id: string;
  title: string;
  covers: string;
  why: string;
  selected_default: boolean;
}

export interface DiscussionUncertainty {
  id: string;
  question: string;
  options: string[];
  default: string;
}

export interface DiscussionHistoryProposal {
  id: string;
  kind: string;
  label: string;
  rationale: string;
  default: boolean;
  topic_id: string;
}

export interface DiscussionRoleTopicNote {
  label: string;
  why: string;
  scope_flag: string;
  scope_suggestion: string;
}

export interface DiscussionRoleNote {
  role: string;
  role_label: string;
  flags: string[];
  structure_notes: string[];
  topic_notes: DiscussionRoleTopicNote[];
}

export interface DiscussionOutput {
  set_title: string;
  summary_blocks: DiscussionSummaryBlock[];
  goals: string[];
  topics: DiscussionTopic[];
  info_cards: DiscussionInfoCard[];
  uncertainties: DiscussionUncertainty[];
  history_proposals: DiscussionHistoryProposal[];
  discussion_notes: DiscussionRoleNote[];
}

export interface DiscussionUserResponse {
  topic_decisions: {
    topic_id: string;
    keep: boolean;
    depth?: Depth;
    question_count?: number;
  }[];
  info_card_decisions: { info_card_id: string; selected: boolean }[];
  bulk_focus_decisions: {
    topic_id: string;
    drop_bulk_indices: number[];
    emphasize_question_types: string[];
  }[];
  history_decisions: { proposal_id: string; accept: boolean }[];
  uncertainty_decisions: { uncertainty_id: string; choice: string }[];
  trim_total_questions?: number;
}
